<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import GameHUD from '@ui/components/GameHUD.svelte';
  import SceneEditor from '@/scene-editor/ui/SceneEditor.svelte';
  import LoadingScreen from '@ui/components/LoadingScreen.svelte';
  import { eventBus } from '@/engine/events/EventBus';
  import { logger } from '@/engine/logging';
  import { layoutState } from '@/stores/ui/layout';
  import { panelPositioningService } from '@/engine/ui';
  import { appMode } from '@/stores/ui/appState';
  import { loadingProgressService } from '@/engine/loading/LoadingProgressService';

  let unsubscribe: (() => void)[] = [];
  let showLoadingScreen = !import.meta.hot; // Не показываем при HMR
  let loadingProgress = loadingProgressService.getProgress();

  onMount(() => {
    logger.debug('🚀 App mounted');

    // Подписываемся на события pixi-resize
    unsubscribe.push(eventBus.on('pixi-resize', handlePixiResize));

    // Подписываемся на прогресс загрузки
    unsubscribe.push(
      loadingProgressService.subscribe(progress => {
        loadingProgress = progress;
      })
    );

    // Подписываемся на завершение загрузки
    unsubscribe.push(eventBus.on('loading-complete', handleLoadingComplete));

    // Dispatch app ready event for game integration
    // Небольшая задержка для стабилизации DOM при HMR
    setTimeout(() => {
      eventBus.emit('app-ready');
      logger.debug('📡 App-ready event emitted', { source: 'App.svelte' });
    }, 50);
  });

  onDestroy(() => {
    // Отписываемся от всех событий
    unsubscribe.forEach(fn => fn());
  });

  function handlePixiResize(data: {
    layout: any;
    gameWidth: number;
    gameHeight: number;
    scale: number;
    scaleUI: number;
    safe: any;
  }) {
    const { layout } = data;

    layoutState.setKey('currentLayout', layout);
    panelPositioningService.updateLayout(layout);
  }

  function handleLoadingComplete() {
    logger.info('🎉 Loading complete, hiding loading screen', { source: 'App.svelte' });
    // Небольшая задержка для плавности, затем скрываем экран загрузки
    setTimeout(() => {
      showLoadingScreen = false;
    }, 1500);
  }

  function handleLoadingReady() {
    logger.info('🎯 Loading screen ready to hide', { source: 'App.svelte' });
    showLoadingScreen = false;
  }
</script>

<main class="app">
  <!-- Loading Screen -->
  {#if showLoadingScreen}
    <LoadingScreen
      progress={loadingProgress.progress}
      status={loadingProgress.status}
      isVisible={showLoadingScreen}
      onLoadingReady={handleLoadingReady}
    />
  {/if}

  <!-- Main App Content -->
  {#if !showLoadingScreen}
    {#if $appMode === 'game'}
      <GameHUD />
    {:else if $appMode === 'scene-editor'}
      <SceneEditor />
    {:else if $appMode === 'auto-battle'}
      <!-- For now reuse GameHUD for auto-battle; dedicated UI can be added later -->
      <GameHUD />
    {/if}
  {/if}
</main>

<style>
  :global(body) {
    margin: 0;
    padding: 0;
    background: var(--color-dark-bg);
    font-family: var(--font-mono);
    overflow: hidden;
  }

  .app {
    width: 100vw;
    height: 100vh;
    position: relative;
  }
</style>
